import { Box, Button, Divider, InputAdornment, TextField, Typography, useTheme } from "@material-ui/core";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { firstLetterUppercase } from "../../utils/first-letter-uppercase";
import { isEmptyValue } from "../../utils/is-empty-value";
import { SliderWithBars } from "../SliderWithBars";
import { isFilterEmpty, NumericFilterState, TableFilterAction } from "./models";

function NumericListFilter(props: { filter: NumericFilterState; usePopover: boolean; dispatcher: (action: TableFilterAction) => void }) {
	const { t } = useTranslation();
	const { palette } = useTheme();

	const bars = useMemo(
		() =>
			props.usePopover
				? Array.from({ length: (props.filter.rangeCounters?.length || 0) / 2 }).map(
						(_, i) => props.filter.rangeCounters![2 * i].count + props.filter.rangeCounters![2 * i + 1].count
				  )
				: props.filter.rangeCounters?.map((counter) => counter.count) || [],
		[props.filter.rangeCounters, props.usePopover]
	);

	function dispatch(startValue: number | null | undefined, endValue: number | null | undefined) {
		props.dispatcher({ type: "set-values", path: props.filter.field as string, startValue, endValue });
	}

	function handleChangeStartValue(value: number) {
		dispatch(isEmptyValue(value) ? null : +value, isEmptyValue(props.filter.endValue) ? props.filter.max : props.filter.endValue);
	}

	function handleChangeEndDate(value: number) {
		dispatch(isEmptyValue(props.filter.startValue) ? props.filter.min : props.filter.startValue, isEmptyValue(value) ? null : +value);
	}

	return (
		<Box pb={1}>
			<Box display="flex" justifyContent="space-between" alignItems="center" px={4} py={3}>
				<TextField
					type="number"
					variant="outlined"
					label={firstLetterUppercase(t("min"))}
					InputLabelProps={{
						shrink: true,
					}}
					InputProps={{
						endAdornment: props.filter.currencyCode && (
							<InputAdornment position="end">{props.filter.currencyCode}</InputAdornment>
						),
					}}
					inputProps={{
						style: {
							color: palette.primary.main,
						},
						min: props.filter.min,
					}}
					value={isEmptyValue(props.filter.startValue) ? "" : props.filter.startValue}
					onChange={(e) => handleChangeStartValue(e.target.value as unknown as number)}
				/>
				<Box mx={2}>—</Box>
				<TextField
					type="number"
					variant="outlined"
					label={firstLetterUppercase(t("max"))}
					InputLabelProps={{
						shrink: true,
					}}
					InputProps={{
						endAdornment: props.filter.currencyCode && (
							<InputAdornment position="end">{props.filter.currencyCode}</InputAdornment>
						),
					}}
					inputProps={{
						style: {
							color: palette.primary.main,
						},
						max: props.filter.max,
					}}
					value={isEmptyValue(props.filter.endValue) ? "" : props.filter.endValue}
					onChange={(e) => handleChangeEndDate(e.target.value as unknown as number)}
				/>
			</Box>
			<SliderWithBars
				min={props.filter.min ?? 0}
				max={props.filter.max ?? 0}
				values={[props.filter.startValue ?? props.filter.min ?? 0, props.filter.endValue ?? props.filter.max ?? 0]}
				onChange={(values) => dispatch(...values)}
				chartHeightPx={!props.filter.isWithoutBars ? 100 : 0}
				bars={(!props.filter.isWithoutBars && bars) || []}
			/>
			{props.usePopover && (
				<>
					<Divider />
					<Button
						variant="text"
						color="primary"
						style={{ textTransform: "none" }}
						disabled={isFilterEmpty(props.filter)}
						onClick={() => props.dispatcher({ type: "clear-selection", path: props.filter.field as string })}
					>
						<Box mt={1}>
							<Typography variant="caption" color="primary">
								{t("clear-filter")}
							</Typography>
						</Box>
					</Button>
				</>
			)}
		</Box>
	);
}

export default memo(NumericListFilter);
